import { useEffect, useMemo } from 'react';
import { TableDataState, TableFilters, TblStats, TblView } from '../table.types';
import { tableDataFilterItems } from '../utils/tableDataFilterItems';
import { tableDataSortItems } from '../utils/tableDataSortItems';
import { tableDataPageItems } from '../utils/tableDataPageItems';

type TableDataHookProps<TItem extends object> = {
    view: TblView;
    filters: TableFilters;
    stats: TblStats;
    data: TableDataState<TItem>;
    setStats: (fn: (draft: TblStats) => void) => void;
};

type TableDataHookReturn<TItem extends object> = {
    items: TItem[];
};

export const useTableData = <TItem extends object>({
    view,
    filters,
    stats,
    data,
    setStats,
}: TableDataHookProps<TItem>): TableDataHookReturn<TItem> => {
    const filtered = useMemo(() => {
        return tableDataFilterItems<TItem>(data.dataItems, view.cols, filters);
    }, [data.dataItems, view.cols, filters]);

    const sorted = useMemo(() => {
        return tableDataSortItems<TItem>(filtered, view.cols, view.sort);
    }, [filtered, view.cols, view.sort]);

    const paged = useMemo(() => {
        return tableDataPageItems<TItem>(sorted, stats.paging.size, stats.paging.page);
    }, [sorted, stats.paging.size, stats.paging.page]);

    useEffect(() => {
        setStats(draft => {
            draft.fltStats.filtered = filtered.length;
            draft.fltStats.total = data.dataItems.length;
            draft.paging.total = Math.ceil(filtered.length / draft.paging.size);
        });
    }, [filtered.length, data.dataItems.length, setStats, stats.paging.size]);

    return {
        items: paged,
    };
};
